
import ApiService from './api.service'
import { TokenService } from './storage.service'
import router from '@/router'
import store from '@/store'

class AuthenticationError extends Error {
  constructor (errorCode, message) {
    super(message)
    this.name = this.constructor.name
    this.message = message
    this.errorCode = errorCode
  }
}

const UserService = {
  /**
     * Login the user and store the access token to TokenService.
     *
     * @returns access_token
     * @throws AuthenticationError
    **/
  login: async function (email, password) {
    const requestData = {
      method: 'post',
      url: 'api/oauth/token',
      data: {
        grant_type: 'password',
        username: email,
        password: password,
        client_id: process.env.VUE_APP_CLIENT_ID
      }
    }

    try {
      const response = await ApiService.customRequest(requestData)
      return await UserService.startSession(response.data.access_token, response.data.refresh_token)
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data.detail)
    }
  },

  /**
     * Stores the tokens and loads the current user, after a login.
     *
     * @returns access_token
     * @throws AuthenticationError (403) when the user belongs to no organisation
    **/
  startSession: async function (accessToken, refreshToken) {
    TokenService.saveToken(accessToken)
    if (refreshToken) {
      TokenService.saveRefreshToken(refreshToken)
    } else {
      TokenService.removeRefreshToken()
    }
    ApiService.setHeader()

    const userResponse = await UserService.getUser()

    if (!userResponse.data.clients?.length) {
      TokenService.removeToken()
      TokenService.removeRefreshToken()
      ApiService.removeHeader()
      throw new AuthenticationError(403, 'noOrganisations')
    }

    store.commit('currentUser', userResponse.data)

    // NOTE: We haven't covered this yet in our ApiService
    //       but don't worry about this just yet - I'll come back to it later
    ApiService.mountInterceptors()

    return accessToken
  },

  /**
     * The users a tester can log in as on a review environment (VUE_APP_REVIEW_LOGIN_AS), one per role.
    **/
  reviewLoginAsPersonas () {
    return ApiService.get('api/auth/review-login-as')
  },

  /**
     * "Login as" of the review environments: logs in as the seeded user of a persona, without a password.
     *
     * @returns access_token
     * @throws AuthenticationError
    **/
  loginAs: async function (persona) {
    try {
      const response = await ApiService.post('api/auth/review-login-as', { persona })

      return await UserService.startSession(response.data.access_token, null)
    } catch (error) {
      if (error instanceof AuthenticationError) {
        throw error
      }
      throw new AuthenticationError(error.response?.status, error.response?.data?.message)
    }
  },

  // eslint-disable-next-line camelcase
  register: async function (token, { firstName, lastName, password, password_confirmation }) {
    const requestData = {
      method: 'post',
      url: 'api/v2/admin/invites/accept',
      data: {
        token: token,
        firstName: firstName,
        lastName: lastName,
        password: password,
        password_confirmation: password_confirmation
      }
    }

    return ApiService.customRequest(requestData)
  },

  /**
     * Refresh the access token.
    **/
  refreshToken: async function () {
    const refreshToken = TokenService.getRefreshToken()
    const requestData = {
      method: 'post',
      url: 'api/oauth/token',
      data: {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: process.env.VUE_APP_CLIENT_ID,
        client_secret: process.env.VUE_APP_CLIENT_SECRET
      }
    }

    try {
      const response = await ApiService.customRequest(requestData)

      TokenService.saveToken(response.data.access_token)
      TokenService.saveRefreshToken(response.data.refresh_token)
      // Update the header in ApiService
      ApiService.setHeader()

      return response.data.access_token
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data.detail)
    }
  },

  getUser () {
    return ApiService.get('api/auth/user')
  },

  setCurrentSeason (data) {
    return ApiService.post('api/v1/admin/user/editSeason', data)
  },
  /**
     * Logout the current user by removing the token from storage.
     *
     * Will also remove `Authorization Bearer <token>` header from future requests.
    **/
  logout () {
    // Remove the token and remove Authorization header from Api Service as well
    TokenService.removeToken()
    TokenService.removeRefreshToken()
    ApiService.removeHeader()

    ApiService.unmountInterceptors()
    router.push({ name: 'login' })
  }
}

export default UserService

export { UserService, AuthenticationError }
